import { KompisElement, property } from '@inter-ikea-kompis/web-component';

import CollapsibleStyle from './CollapsibleStyle.js';
/**
 * This component collapse and open content with a transition.
 *
 * @event animationEnd When area has been fully expanded or fully collapsed
 *
 * @slot Content to be collapsed.
 */

export default class Collapsible extends KompisElement {
	// Open state. true => height 0. false => size of its content
	@property({ type: Boolean })
	public expanded = false;

	// If true, it will expand horizontally instead of vertically
	@property({ type: Boolean })
	public horizontal = false;

	private isAnimationCompleted = true;
	private listeners = {
		animationend: this.onAnimationEnd.bind(this)
	};
	private elements = {
		slot: document.createElement('slot'),
		container: document.createElement('div'),
		style: document.createElement('style')
	};
	private isRendered = false;

	public override connectedCallback(): void {
		super.connectedCallback();
		this.elements.container.classList.add('container');
		this.elements.container.appendChild(this.elements.slot);
		this.shadowRoot?.appendChild(this.elements.style);
		this.shadowRoot?.appendChild(this.elements.container);
		this.elements.container.addEventListener('animationend', this.listeners.animationend);
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.isRendered = false;
		this.removeEventListener('animationend', this.listeners.animationend);
	}
	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('expanded') && this.isRendered) {
			this.isAnimationCompleted = false;
		}
		if (changedProperties.has('expanded')) {
			this.elements.container.appendChild(this.elements.slot);
		}
		this.isRendered = true;
		this.updateStyles();
		return false;
	}

	/**
	 * Update CSS code in the style tag.
	 */
	private updateStyles(): void {
		this.elements.style.innerHTML = CollapsibleStyle.getStyle({
			isAnimationCompleted: this.isAnimationCompleted,
			expanded: this.expanded,
			horizontal: this.horizontal,
			contentSize: this.horizontal
				? this.elements.container.scrollWidth
				: this.elements.container.scrollHeight
		});
	}

	/**
	 * Callback for animationend.
	 */
	private onAnimationEnd(): void {
		this.isAnimationCompleted = true;
		this.updateStyles();
		if (!this.expanded) {
			this.elements.container.removeChild(this.elements.slot);
		}
		this.dispatchEvent(new CustomEvent('animationEnd', { bubbles: true }));
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-collapsible': Collapsible;
	}
}
